import { orgQueryKeys, studyQueryKeys, userQueryKeys } from '@/config/queryKeys';
import { revokeStudyAccess } from '@/config/studies';
import { defaultColDef } from '@/constants';
import { useDebounce } from '@/hooks/useDebounce';
import { apiClient } from '@/lib/api-client';
import { AutoComplete } from '@nautilusbio/nb-design-system';
import { Button } from '@nautilusbio/nb-design-system/Button';
import { getTextClassName } from '@nautilusbio/nb-design-system/helpers';
import { Stack, getStackClassName } from '@nautilusbio/nb-design-system/Stack';
import { Table } from '@nautilusbio/nb-design-system/Table';
import { Text } from '@nautilusbio/nb-design-system/Text';
import { toast } from '@nautilusbio/nb-design-system/Toaster';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ColDef } from 'ag-grid-enterprise';
import clsx from 'clsx';
import { UserPlus } from 'lucide-react';
import React, { FC, Key, useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import styles from '../styles.module.css';
import { SET_FILTER_PARAMS, TEXT_FILTER_PARAMS } from '../utils/sharedColumnDefs';
import { showUserMutationToast } from '../utils/userMutationToasts';

import { ActionsCellRenderer } from './ActionsCellRenderer';
import { OwnerCellRenderer } from './OwnerCellRenderer';

/**
 * Represents a user with access to a study
 */
interface AllowedUser {
  id: string;
  full_name: string;
  is_owner: boolean;
}

/**
 * User search result from the API
 */
interface UserSearchResult {
  id: string;
  email: string;
  full_name: string;
}

/**
 * Props for the UserManagementTable component
 */
interface UserManagementTableProps {
  /** List of users currently allowed to access the study */
  allowedUsers: AllowedUser[];
  /** Whether the current user has permission to manage other users */
  canManage: boolean;
  /** Organization ID */
  orgId: string;
  /** Study ID */
  studyId: string;
}

/**
 * User Management Table Component
 *
 * Displays and manages users who have access to a study.
 * Allows owners to:
 * - Add new users to the study
 * - Promote users to owner status
 * - Revoke user access
 *
 * Features:
 * - AG Grid for consistent table rendering with advanced filtering
 * - Column header menu filters (no floating filter row for clean UI)
 * - Text filter for user names
 * - Set Filter for is_owner column (boolean categorical data)
 * - Custom cell renderers for owner status and actions
 * - Real-time user search with autocomplete
 *
 * @param props - Component props
 * @returns Rendered user management table with search and actions
 */
export const UserManagementTable: FC<UserManagementTableProps> = ({ allowedUsers, canManage, orgId, studyId }) => {
  const { t } = useTranslation('general');
  const queryClient = useQueryClient();
  // separate immediate input value from debounced query string
  const [inputValue, setInputValue] = useState('');
  const [selectedUser, setSelectedUser] = useState<UserSearchResult | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const queryKey = studyQueryKeys.detail(orgId, studyId);

  // Search for users in the organization
  const debouncedSearchQuery = useDebounce(inputValue, 300);
  const { data: searchResults = [], isFetching } = useQuery<UserSearchResult[]>({
    // include orgId in the cache key so results aren't shared across orgs
    queryKey: [...orgQueryKeys.users(orgId), 'search', debouncedSearchQuery],
    queryFn: async ({ signal }) => {
      if (!debouncedSearchQuery || debouncedSearchQuery.length < 2) {
        return [];
      }
      try {
        // Use the centralized endpoint builder to avoid typos and ensure consistency
        const url = `${userQueryKeys.endpoints.orgUsers(orgId)}/search`;
        const response = await apiClient.post<UserSearchResult[]>(
          url,
          { q: debouncedSearchQuery, limit: 50 },
          { signal },
        );
        return response || [];
      } catch (error: any) {
        if (error?.name === 'AbortError') {
          return [];
        }
        throw error;
      }
    },
    enabled: debouncedSearchQuery.length >= 2,
    staleTime: 30000,
  });

  // Grant access mutation
  const grantAccessMutation = useMutation({
    mutationFn: async (userId: string) => {
      return await apiClient.post(studyQueryKeys.endpoints.users(orgId, studyId), {
        user_ids: [userId],
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
      setInputValue('');
      setSelectedUser(null);
    },
    onError: (error: Error) => {
      console.error('Failed to grant access:', error);
    },
  });

  // Revoke access mutation (delegates to pure helper)
  const revokeAccessMutation = useMutation({
    mutationFn: async (userId: string) => {
      await revokeStudyAccess(orgId, studyId, userId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
    },
    onError: (error: unknown) => {
      console.error('Failed to revoke access:', error);
    },
  });

  // Promote to owner mutation
  const promoteToOwnerMutation = useMutation({
    mutationFn: async (userId: string) => {
      return await apiClient.post(studyQueryKeys.endpoints.owners(orgId, studyId), {
        user_ids: [userId],
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
    },
    onError: (error: Error) => {
      console.error('Failed to promote to owner:', error);
    },
  });

  /**
   * Handles granting access to a selected user
   */
  const handleGrantAccess = useCallback(() => {
    if (!selectedUser) {
      toast.error(t('user_management.select_user_error', { ns: 'general' }));
      return;
    }

    const grantPromise = grantAccessMutation.mutateAsync(selectedUser.id);

    showUserMutationToast(grantPromise, t, 'adding', selectedUser.full_name);

    // Clear selection after successful mutation
    return grantPromise.then(() => {
      setInputValue('');
      setSelectedUser(null);
      return undefined;
    });
  }, [selectedUser, grantAccessMutation, t]);

  /**
   * Handles revoking access for a user
   */
  const handleRevokeAccess = useCallback(
    (userId: string) => {
      const affectedUser = allowedUsers.find(u => u.id === userId);
      const revokePromise = revokeAccessMutation.mutateAsync(userId);

      showUserMutationToast(revokePromise, t, 'revoking', affectedUser?.full_name);
    },
    [allowedUsers, revokeAccessMutation, t],
  );

  /**
   * Handles promoting a user to owner status
   */
  const handlePromoteToOwner = useCallback(
    (userId: string) => {
      const affectedUser = allowedUsers.find(u => u.id === userId);
      const promotePromise = promoteToOwnerMutation.mutateAsync(userId);

      showUserMutationToast(promotePromise, t, 'promoting', affectedUser?.full_name);
    },
    [allowedUsers, promoteToOwnerMutation, t],
  );

  /**
   * Convert search results to autocomplete options, filtering out already allowed users
   */
  const userOptions = useMemo(
    () =>
      searchResults
        .filter(user => !allowedUsers.some(u => u.id === user.id))
        .map(user => ({
          className: getTextClassName('md'),
          key: user.id,
          id: user.id,
          label: `${user.full_name} (${user.email})`,
          value: user.id,
        })),
    [searchResults, allowedUsers],
  );

  /**
   * Memoized cell renderer for the actions column
   */
  const ActionsCellRendererWithCallbacks = useCallback(
    (props: { data: AllowedUser }) => (
      <ActionsCellRenderer
        canManage={canManage}
        containerRef={containerRef}
        data={props.data}
        onPromoteToOwner={handlePromoteToOwner}
        onRevokeAccess={handleRevokeAccess}
        t={t}
      />
    ),
    [canManage, handlePromoteToOwner, handleRevokeAccess, t],
  );

  /**
   * AG Grid column definitions with enhanced filtering capabilities
   * - Text filter for user names (accessible via column header menu)
   * - Set filter for is_owner boolean column (accessible via column header menu)
   */
  const columnDefs = useMemo<ColDef<AllowedUser>[]>(() => {
    const cols: ColDef<AllowedUser>[] = [
      {
        field: 'full_name',
        headerName: t('name', { ns: 'general' }),
        flex: 2,
        sortable: true,
        filter: 'agTextColumnFilter',
        filterParams: TEXT_FILTER_PARAMS,
        minWidth: 150,
        resizable: true,
      },
      {
        field: 'is_owner',
        headerName: t('user_management.owner', { ns: 'general' }),
        flex: 1,
        sortable: true,
        // Use Set Filter for boolean column - provides checkbox list UI
        filter: 'agSetColumnFilter',
        filterParams: {
          ...SET_FILTER_PARAMS,
          suppressSorting: true,
          // Use actual booleans to match the cell data type, avoiding string/boolean
          // type mismatch in AG Grid's client-side set filter comparisons.
          values: [true, false],
          valueFormatter: ({ value }: { value: boolean }) =>
            value ? t('yes', { ns: 'general' }) : t('no', { ns: 'general' }),
        },
        cellStyle: { padding: 0 },
        cellRenderer: OwnerCellRenderer,
        minWidth: 100,
        resizable: true,
      },
    ];

    if (canManage) {
      cols.push({
        headerName: t('actions', { ns: 'general' }),
        flex: 1,
        cellRenderer: ActionsCellRendererWithCallbacks,
        sortable: false,
        filter: false,
        minWidth: 120,
        resizable: false,
      });
    }

    return cols;
  }, [canManage, ActionsCellRendererWithCallbacks, t]);
  const autoCompleteLabel = {
    children: t('search_user', { ns: 'general' }),
    id: 'user-management-search-label',
  };

  const onInputChangeHandler = useCallback((value: string) => {
    // update UI immediately but only query after debounce
    setInputValue(value);
  }, []);

  const onSelectionChangeHandler = useCallback(
    (key: Key | null) => {
      if (key) {
        const userObj = searchResults.find(u => u.id === String(key));
        setSelectedUser(userObj || null);
      } else {
        setSelectedUser(null);
      }
    },
    [searchResults],
  );

  return (
    <Stack
      className={styles.studyUserTableContainer}
      fullHeight
      fullWidth
      gap={8}
      orientation="vertical"
      ref={containerRef}
      role="region"
      aria-label={t('user_management.allowed_users', { ns: 'general' })}
    >
      <Stack alignItems="center" fullWidth gap={2} justifyContent="space-between" orientation="horizontal">
        {canManage && (
          <Stack alignItems="center" fullWidth gap={4} orientation="horizontal">
            <AutoComplete
              containerClassName={styles.studyUsersAutoComplete}
              data-testid="add-user-autocomplete"
              label={autoCompleteLabel}
              isLoading={isFetching}
              onInputChange={onInputChangeHandler}
              onSelectionChange={onSelectionChangeHandler}
              options={userOptions}
              placeholder={t('begin_typing_here', { ns: 'general' })}
              selectedKey={selectedUser?.id ?? null}
              size="sm"
              value={inputValue}
            />

            <Button
              aria-label={t('add_user', { ns: 'general' })}
              className={clsx(getStackClassName('displayflex'), getStackClassName('aicenter'))}
              data-testid="add-user-button"
              isDisabled={!selectedUser || grantAccessMutation.isPending}
              onPress={handleGrantAccess}
              size="lg"
              visual="primary"
            >
              <UserPlus size={16} aria-hidden="true" />
              <Text as="span" className={styles.studyUserActionText}>
                {grantAccessMutation.isPending ? t('adding', { ns: 'general' }) : t('add_user', { ns: 'general' })}
              </Text>
            </Button>
          </Stack>
        )}
      </Stack>

      <Stack fullWidth gap={4} orientation="vertical">
        <Text as="strong" className={styles.studyUsersTitle}>
          {t('user_management.allowed_users', { ns: 'general' })} ({allowedUsers.length})
        </Text>

        <Table<AllowedUser>
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          rowData={allowedUsers}
          domLayout="autoHeight"
          enableAdvancedFilter={false}
          suppressContextMenu={true}
          tableId="user-management-table"
        />
      </Stack>
    </Stack>
  );
};
